/*
 * Copyright © 2015-2021 Serenova, LLC. All rights reserved.
 */

/**
 *
 * QueuesDetailsPanel
 *
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import styled, { createGlobalStyle, css } from 'styled-components';

import store from '../../../redux/store';
import { Button, Pagination, LoadingSpinnerSVG } from 'cx-ui-components';
import { createQueueVersionCopy } from '../../../redux/modules/entities';
import { setShowAdvancedOrBasicQueueQuery } from '../../../redux/modules/entities/index';
import { getQueuesPageDependentEntities } from '../../../redux/modules/entities/queues/selectors';

// React-Table does not integrate well with Styled components
// We will be writing table style overrides here
const GlobalStyle = createGlobalStyle`
  .QueuesSidePanelTable .rt-thead {
    box-shadow: none !important;
    border-bottom: 1px solid #e6e6e6 !important;
  }
  .QueuesSidePanelTable .rt-thead .rt-th {
    color: #656565 !important;
    padding: 10px !important;
    font-size: 14px !important;
    font-weight: 600 !important;
    text-align: left !important;
    border: none !important;
    min-width: 100px !important;
    :not(.rt-resizable-header) {
      padding-left: 5px;
      margin-bottom 5px;
    }
  }
  .QueuesSidePanelTable .rt-tbody .rt-tr-group {
    border: none !important;
  }
  .QueuesSidePanelTable .rt-tbody .rt-tr-group .rt-tr {
    &:hover {
      background-color: #e6f5ff;
    }
  }
  .QueuesSidePanelTable .rt-tbody .rt-td {
    padding: 10px;
    font-size: 14px;
    text-align: left;
    border: none;
    min-width: 100px;
    cursor: pointer;
  }
  .QueuesSidePanelTable .sub-component {
    &:hover {
      background-color: #e6f5ff;
    }
  }
  .QueuesSidePanelTable .-pagination {
    box-shadow: none;
    border-top: 1px solid #e6e6e6;
  }
  .QueuesSidePanelTable .-pagination .-center {
    -ms-flex: inherit;
    flex: inherit;
  }
}`;
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const VersionContainer = styled.div`
  position: relative;
  font-size: 14px;
  color: #999999;
  padding-bottom: 10px;
  :hover {
    background-color: #e6f5ff;
  }
`;
const QueryWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const QueryHeading = styled.span`
  display: inline-block;
  margin-top: 15px;
  margin-left: 25px;
  width: 140px;
  font-weight: 600;
`;
const SwitchQueryTypeLink = styled.span`
  display: inline-block;
  margin-top: 15px;
  position: absolute;
  right: 10px;
  color: #3498db;
  cursor: pointer;
  :hover {
    color: #07487a;
  }
`;
const AfterSecondsInQueueText = styled.span`
  display: inline-block;
  margin-top: 10px;
  margin-left: 25px;
  font-weight: 700;
`;
const AdvacnedQueryText = styled.textarea`
  height: 90px;
  color: inherit;
  font-size: 13px;
  padding: 7px;
  cursor: default;
  disabled: true;
  border-radius: 3px;
  margin: 10px 10px 0 165px;
  outline: none !important;
  border: 1px solid transparent;
  box-shadow: 0 1px 2px rgb(0 0 0 / 20%) inset, 0 -1px 0 rgb(0 0 0 / 5%) inset;
`;
const QueryTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 5px 5px 0 20px;
`;
const QueryTextWrapper = styled.div`
  display: flex;
  margin: 5px;
`;
const QueryTextLabel = styled.span`
  min-width: 130px;
  max-width: 130px;
  margin-top: 8px;
`;
const QueryTextDiv = styled.div`
  display: inline-block;
`;
const QueryText = styled.span`
  display: inline-block;
  border: 1px solid #cccccc;
  border-radius: 5px;
  margin-left: 5px;
  padding: 5px;
  background: #ebebeb;
  white-space: pre-wrap;
  margin-top: 5px;
`;
const PriorityComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 20px 10px 0 25px;
`;
const PriorityFieldWrapper = styled.div`
  display: flex;
  margin-bottom: 10px;
`;
const PriorityFieldLabel = styled.label`
  display: inline-block;
  min-width: 140px;
  max-width: 140px;
  margin-top: 5px;
`;
const PriorityInput = styled.input`
  flex-grow: 1;
  height: 32px;
  font-size: 13px;
  color: #999999;
  cursor: default;
  border: 1px solid transparent;
  border-radius: 3px;
  background-color: #fafafa;
  ${(props) =>
    props.label === 'Queue SLA' &&
    css`
      padding-left: 10px;
    `};
  ${(props) =>
    props.label !== 'Queue SLA' &&
    css`
      padding-right: 10px;
      text-align: right;
    `};
  box-shadow: 0 1px 2px rgb(0 0 0 / 20%) inset, 0 -1px 0 rgb(0 0 0 / 5%) inset;
`;
const StyledButton = styled(Button)`
  float: right;
  margin-right: 10px;
`;

const PriorityFieldInput = ({ label, inputVal }) => (
  <PriorityFieldWrapper>
    <PriorityFieldLabel>{label}</PriorityFieldLabel>
    <PriorityInput label={label} value={inputVal} disabled />
  </PriorityFieldWrapper>
);

const ListItemField = ({ index, entity, allOrAny, dataSet }) => (
  <Fragment>
    {dataSet && (
      <QueryTextWrapper>
        <QueryTextLabel>{`${allOrAny} of these ${entity === 'user-id' ? 'users: ' : `${entity}: `}`}</QueryTextLabel>
        <QueryTextDiv>
          {dataSet.map((set, idx) => <QueryText key={`${entity}-${allOrAny}-${index}-${idx}`}>{set}</QueryText>)}
        </QueryTextDiv>
      </QueryTextWrapper>
    )}
  </Fragment>
);

const SortListItemField = ({ index, entity, dataSet }) => (
  <Fragment>
    {dataSet && (
      <QueryTextWrapper>
        <QueryTextLabel>{`Sort by ${entity}: `}</QueryTextLabel>
        <QueryTextDiv>
          {dataSet.map((set, idx) => <QueryText key={`${entity}-sort-${index}-${idx}`}>{set}</QueryText>)}
        </QueryTextDiv>
      </QueryTextWrapper>
    )}
  </Fragment>
);

const SubComponent = ({ original }) => {
  const BasicQueryComponent =
    original &&
    original.basicQuery &&
    original.basicQuery.map((level, index) => {
      const { skills, groups, users } = getQueuesPageDependentEntities(store.getState());

      const secondsInQueue =
        level &&
        level.map &&
        level.map.find((levelItem) => levelItem.find((inQueueItem) => inQueueItem['key'] === 'after-seconds-in-queue'));

      const filters =
        level &&
        level.map &&
        level.map.find((levelItem) => levelItem.find((queryItem) => queryItem['key'] === 'query'));
      const sortFilters =
        level &&
        level.map &&
        level.map.find((levelItem) => levelItem.find((queryItem) => queryItem['key'] === 'sort'));

      const filterItems = filters && filters[1] && filters[1]['map'];
      const sortFilterItems = sortFilters && sortFilters[1] && sortFilters[1][0]['list'];

      const FiltersList =
        filterItems &&
        filterItems.map((filter, filterIndex) => {
          const entityFilter = filter && filter.find((f) => f.key);
          const allList = filter[1] && filter[1].find((lists) => lists.list[0].sym === 'every');
          const anyList = filter[1] && filter[1].find((lists) => lists.list[0].sym === 'some');

          if (entityFilter && entityFilter.key === 'skills') {
            const allListItems =
              allList &&
              allList.list[1]['map'].reduce((acc, item) => {
                const selectedSkill = skills && skills.filter((skill) => skill.id === item[0].val)[0];
                const skillName = selectedSkill && selectedSkill.name;
                acc.push(`${skillName} (${item[1].list[0].sym} ${item[1].list[1]})`);
                return acc;
              }, []);

            const anyListItems =
              anyList &&
              anyList.list[1]['map'].reduce((acc, item) => {
                const selectedSkill = skills && skills.filter((skill) => skill.id === item[0].val)[0];
                const skillName = selectedSkill && selectedSkill.name;
                acc.push(`${skillName} (${item[1].list[0].sym} ${item[1].list[1]})`);
                return acc;
              }, []);

            return (
              <QueryTextContainer key={`${entityFilter.key}-${index}-${filterIndex}`}>
                {allListItems && (
                  <ListItemField entity={entityFilter.key} dataSet={allListItems} allOrAny="All" index={index} />
                )}
                {anyListItems && (
                  <ListItemField entity={entityFilter.key} dataSet={anyListItems} allOrAny="Any" index={index} />
                )}
              </QueryTextContainer>
            );
          } else if (entityFilter && (entityFilter.key === 'groups' || entityFilter.key === 'user-id')) {
            let allListItems = allList && allList.list[1]['set'].map((item) => item.val);
            let anyListItems = anyList && anyList.list[1]['set'].map((item) => item.val);
            if (entityFilter.key === 'groups') {
              allListItems =
                allListItems &&
                allListItems.map((itemVal) => {
                  const selectedGroup = groups && groups.filter((group) => group.id === itemVal)[0];
                  return selectedGroup && selectedGroup.name;
                });
              anyListItems =
                anyListItems &&
                anyListItems.map((itemVal) => {
                  const selectedGroup = groups && groups.filter((group) => group.id === itemVal)[0];
                  return selectedGroup && selectedGroup.name;
                });
            } else if (entityFilter.key === 'user-id') {
              anyListItems =
                anyListItems &&
                anyListItems.map((itemVal) => {
                  const userInfo = users && users.filter((user) => user.id === itemVal)[0];
                  return userInfo && `${userInfo.firstName} ${userInfo.lastName}`;
                });
            }
            return (
              <QueryTextContainer key={`${entityFilter.key}-${index}-${filterIndex}`}>
                {allListItems && (
                  <ListItemField entity={entityFilter.key} dataSet={allListItems} allOrAny="All" index={index} />
                )}
                {anyListItems && (
                  <ListItemField entity={entityFilter.key} dataSet={anyListItems} allOrAny="Any" index={index} />
                )}
              </QueryTextContainer>
            );
          } else {
            return '';
          }
        });

      const SortFiltersList =
        sortFilterItems &&
        sortFilterItems.map((filterItems, filterIndex) => {
          const sortListItems = [];
          (filterItems.map).map((filter) => {
            const selectedSkill = skills && skills.filter((skill) => skill.id === filter[0].val);
            const skillName = selectedSkill && selectedSkill[0].name;
            let order;
            if (filter[1].key === 'asc') {
              order = '↑';
            } else if (filter[1].key === 'des') {
              order = '↓';
            }
            sortListItems.push(`${order} ${skillName}`);
          });

          if (sortListItems && sortListItems.length > 0) {
            return (
              <QueryTextContainer key={`sort-${index}-${filterIndex}`}>
                {sortListItems && (
                  <SortListItemField entity='skills' dataSet={sortListItems} index={index} />
                )}
              </QueryTextContainer>
            );
          } else {
            return '';
          }
        });

      return (
        <div key={`${original.version}-${index}`}>
          {secondsInQueue[0].key === 'after-seconds-in-queue' &&
            index !== 0 && <AfterSecondsInQueueText>{`After ${secondsInQueue[1]} seconds`}</AfterSecondsInQueueText>}
          {FiltersList}
          {SortFiltersList}
        </div>
      );
    });

  return (
    <VersionContainer className="sub-component">
      {(!original.showQueryType || original.showQueryType === 'basicQuery') && (
        <QueryWrapper>
          <QueryHeading>Basic Query</QueryHeading>
          <SwitchQueryTypeLink
            onClick={(e) => {
              store.dispatch(setShowAdvancedOrBasicQueueQuery(original.queueId, original.version, 'advancedQuery'));
              e.stopPropagation();
            }}>
            (Show advanced query)
          </SwitchQueryTypeLink>
          {BasicQueryComponent}
        </QueryWrapper>
      )}
      {original.showQueryType === 'advancedQuery' && (
        <QueryWrapper>
          <QueryHeading>Advanced Query</QueryHeading>
          <SwitchQueryTypeLink
            onClick={(e) => {
              e.stopPropagation();
              store.dispatch(setShowAdvancedOrBasicQueueQuery(original.queueId, original.version, 'basicQuery'));
            }}>
            (show basic query)
          </SwitchQueryTypeLink>
          <AdvacnedQueryText disabled value={original.query} />
        </QueryWrapper>
      )}
      <PriorityComponentWrapper>
        <PriorityFieldInput label="Min Priority" inputVal={original.minPriority} />
        <PriorityFieldInput label="Max Priority" inputVal={original.maxPriority} />
        <PriorityFieldInput label="Priority Value" inputVal={original.priorityValue} />
        <PriorityFieldInput label="Priority Rate" inputVal={original.priorityRate} />
        <PriorityFieldInput label="Priority Unit" inputVal={original.priorityUnit} />
        <PriorityFieldInput label="Queue SLA" inputVal={original.sla} />
      </PriorityComponentWrapper>
      <StyledButton
        buttonType="secondary"
        data-automation="createVersionCopyBtn"
        disabled={false}
        onClick={() => store.dispatch(createQueueVersionCopy(original))}>
        Create Copy
      </StyledButton>
    </VersionContainer>
  );
};

const columns = [
  {
    Header: 'Version',
    id: 'version',
    accessor: (d) => d.name,
  },
  {
    Header: 'Created On',
    id: 'created',
    accessor: (d) => moment(d.created).format('lll'),
  },
];

class QueuesDetailsPanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      expanded: null,
    };
  }
  componentDidMount() {
    if (this.props.activeQueueVersion && this.props.selectedQueueVersions) {
      this.expandActiveVersionByDefault();
    }
  }
  componentDidUpdate(prevProps) {
    if (this.props.activeQueueVersion !== prevProps.activeQueueVersion && this.props.selectedQueueVersions) {
      this.expandActiveVersionByDefault();
    }
  }
  expandActiveVersionByDefault = () => {
    const { selectedQueueVersions, activeQueueVersion } = this.props;
    const activeQueueVersionIndex =
      selectedQueueVersions && selectedQueueVersions.findIndex((version) => version.version === activeQueueVersion);
    const expanded = {};
    expanded[activeQueueVersionIndex] = true;
    this.setState({ expanded: expanded });
  };
  expandRow(row) {
    const expanded = { ...this.state.expanded };
    if (expanded[row.index]) {
      expanded[row.index] = !expanded[row.index];
    } else {
      Object.keys(expanded).forEach((rowIndex) => {
        if (row.index !== rowIndex) {
          expanded[rowIndex] = false;
        }
      });
      expanded[row.index] = true;
    }
    this.setState({
      expanded: expanded,
    });
  }
  getTableRowProps = (state, rowInfo) => {
    if (rowInfo && rowInfo.row) {
      return {
        onClick: (e) => this.expandRow(rowInfo),
      };
    } else {
      return { style: {} };
    }
  };

  render() {
    const { selectedQueueVersions, isAllDependentEntitiesFetched } = this.props;
    return (
      <Fragment>
        <GlobalStyle/>
        {!isAllDependentEntitiesFetched ? (
          <LoadingSpinnerSVG size={60} />
        ) : (
          <Wrapper id="dtpanel-queues">
            {this.props.children}
            <ReactTable
              columns={columns}
              sortable={false}
              minRows={0}
              data={selectedQueueVersions}
              SubComponent={SubComponent}
              className="QueuesSidePanelTable"
              PaginationComponent={Pagination}
              emptyRowsWhenPaging={false}
              getTdProps={this.getTableRowProps}
              expanded={this.state.expanded}
              noDataText={!selectedQueueVersions && 'No Versions found'}
            />
          </Wrapper>
        )}
      </Fragment>
    );
  }
}

PriorityFieldInput.propTypes = {
  label: PropTypes.string,
  inputVal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ListItemField.propTypes = {
  index: PropTypes.number,
  entity: PropTypes.string,
  allOrAny: PropTypes.string,
  dataSet: PropTypes.array,
};

SubComponent.propTypes = {
  original: PropTypes.object,
};

QueuesDetailsPanel.propTypes = {
  children: PropTypes.any,
  selectedQueueVersions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  activeQueueVersion: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isAllDependentEntitiesFetched: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
};

export default QueuesDetailsPanel;
